// Server function: generate a short FAQ for a blog post using Lovable AI Gateway.
// Ported from the old edge function `generate-blog-faq` — reads post content in
// the requested language, asks the model for 4 Q&A pairs, caches the result on
// blog_posts.faq_<lang>, and returns it.
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

type Lang = "en" | "pl" | "by";
const LANG_NAMES: Record<Lang, string> = {
  en: "English",
  pl: "Polish",
  by: "Belarusian",
};

const InputSchema = z.object({
  postId: z.string().min(1),
  language: z.enum(["en", "pl", "by"]),
});

export interface FAQItem {
  q: string;
  a: string;
}

export const generateBlogFaq = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<{ faq: FAQItem[]; cached: boolean }> => {
    const { postId } = data;
    const lang = data.language as Lang;

    const supabase = createClient(
      process.env['SUPABASE_URL']!,
      process.env['SUPABASE_SERVICE_ROLE_KEY']!,
    );

    const { data: post, error } = await supabase
      .from("blog_posts")
      .select(
        "id, title_en, title_pl, title_by, content_en, content_pl, content_by, faq_en, faq_pl, faq_by",
      )
      .eq("id", postId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post) throw new Error("post not found");

    const cached = post[`faq_${lang}` as keyof typeof post] as unknown;
    if (Array.isArray(cached) && cached.length > 0) {
      return { faq: cached as FAQItem[], cached: true };
    }

    const title =
      (post[`title_${lang}` as keyof typeof post] as string | null) || post.title_en || "";
    const content =
      (post[`content_${lang}` as keyof typeof post] as string | null) || post.content_en || "";

    if (!content || content.trim().length < 50) {
      throw new Error("post content too short");
    }

    const apiKey = process.env['LOVABLE_API_KEY'];
    if (!apiKey) throw new Error("missing LOVABLE_API_KEY");

    const system = `You generate concise FAQs about a blog article. Return STRICT JSON only: {"faq":[{"q":"...","a":"..."}]}. Exactly 4 items. Questions must be things a reader would realistically ask about THIS article. Answers 1-3 sentences, plain text, no markdown, grounded in the article — do not invent facts. Write everything in ${LANG_NAMES[lang]}.`;

    const user = `Article title: ${title}\n\nArticle content:\n${content.slice(0, 8000)}`;

    const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (aiRes.status === 429) throw new Error("rate limit, try later");
    if (aiRes.status === 402) throw new Error("AI credits exhausted");
    if (!aiRes.ok) {
      const t = await aiRes.text();
      throw new Error(`AI gateway error: ${t.slice(0, 500)}`);
    }

    const payload = (await aiRes.json()) as {
      choices?: Array<{ message?: { content?: unknown } }>;
    };
    const raw = payload?.choices?.[0]?.message?.content ?? "";
    let parsed: { faq?: FAQItem[] } = {};
    try {
      parsed = typeof raw === "string" ? JSON.parse(raw) : (raw as { faq?: FAQItem[] });
    } catch {
      throw new Error("AI returned invalid JSON");
    }
    const faq = Array.isArray(parsed.faq)
      ? parsed.faq
          .filter((x) => x && typeof x.q === "string" && typeof x.a === "string")
          .slice(0, 6)
      : [];

    if (faq.length === 0) throw new Error("empty FAQ from AI");

    await supabase
      .from("blog_posts")
      .update({ [`faq_${lang}`]: faq })
      .eq("id", postId);

    return { faq, cached: false };
  });
